import { Modal } from '../../../../components/Modal/Modal'
import React, { useEffect, useState } from 'react'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { useGetIntegrationUsersQuery, useLazyGetIntegrationUsersQuery, useSaveResultMutation } from '../../../../store/integrations/integrations'
import { useGetRoomSessionQuery } from '../../../../store/media/media'
import { AIResultViewModel } from '../../../../apps/professional-app/fa/AIResultMapper'
import { TrackEvent, useTracking } from '../../../../apps/professional-app/fa/common/tracking'
import { FormProvider, useForm } from 'react-hook-form'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../common/errorMessages'
import { ContactFormSelectDropdown } from '../ContactDropdown'
import 'react-datepicker/dist/react-datepicker.css'
import './AddResultToCRMModal.scss'
import { addToast } from '../../../../store/toast/toastSlice'
import { RoomSession } from '../../../../models/Room'
import { getContactOptions } from '../utils'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]

export interface AddNoteToCRMModalProps {
  roomId: string
  sessionId: string
  aiFeatureId: string
  aiResults: AIResultViewModel[]
  show: boolean
  onHide: () => void
  serviceId: string
}

type FormValues = {
  externalUserId: string
}

export const AddNoteToCRMModal: React.FC<AddNoteToCRMModalProps> = ({ show, onHide, roomId, sessionId, aiFeatureId, aiResults, serviceId }) => {
  const _useAppDispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)

  const { tracked } = useTracking()
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { data: roomSession, isFetching: isRoomSessionFetching } = useGetRoomSessionQuery({ accountId, roomId, sessionId }, { refetchOnMountOrArgChange: true })
  const [triggerSaveResults, { isSuccess, isLoading }] = useSaveResultMutation()

  const defaultValues = {
    externalUserId: '',
  }

  const handleOnHide = () => {
    onHide()
  }

  const handleSave = tracked(
    (data: FormValues) => {
      aiResults.forEach((aiResult) =>
        triggerSaveResults({
          accountId,
          connector: serviceId,
          userId: currentUserId,
          body: {
            roomId: roomId,
            sessionId: sessionId,
            aiFeatureId: aiFeatureId,
            aiResultId: aiResult.id,
            externalUserIds: [data.externalUserId],
            extra: {},
          },
        }),
      )
    },
    { eventName: TrackEvent.SAVE_NOTES_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          roomId: roomId,
          sessionId: sessionId,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit } = form

  useEffect(() => {
    if (isSuccess) {
      handleOnHide()
      form.reset()
      _useAppDispatch(addToast({ variant: 'success', title: 'Results saved', messages: ['Results were successfully saved to your CRM'] }))
    }
  }, [form, isSuccess, onHide, _useAppDispatch])

  return (
    <Modal
      size='xl'
      show={show}
      onHide={handleOnHide}
      header={
        <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
          Save your Notes to&nbsp;<span className='text-capitalize'>{serviceId}</span>
        </BootstrapModal.Title>
      }
      body={
        <FormProvider {...form}>
          {!isRoomSessionFetching && roomSession ? (
            <AddNoteToCRMModalBody roomSession={roomSession.data} serviceId={serviceId} />
          ) : (
            <div className='d-flex justify-content-center'>
              <Spinner />
            </div>
          )}
        </FormProvider>
      }
      showFooterActions={true}
      onFooterPrimaryAction={handleSubmit(handleSave)}
      footerPrimaryActionLoading={isLoading}
      onFooterSecondaryAction={handleOnHide}
      centered={true}
    />
  )
}

const AddNoteToCRMModalBody: React.FC<{
  roomSession: RoomSession
  serviceId: string
}> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)
  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })

  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])

  useEffect(() => {
    if (contacts && contacts.data) {
      setContactOptions(getContactOptions(contacts.data, serviceId))
    }
  }, [contacts])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({ accountId, connector: serviceId, filters: { name: searchInput } }).unwrap()
      if (contactsData && contactsData.data) {
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  if (isContactsFetching && !contacts) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid, please reconnect your integration:</Col>
          </Row>
        </Container>
      </>
    )
  }

  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={6}>
          <ContactFormSelectDropdown
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
          />
        </Col>
      </Row>
    </Container>
  )
}
